import * as _ from "lodash";

import { PriceAndPromotionInfo, PromotionLabel } from "../types/PriceAndPromotionInfo";
import {
    BettyBundle,
    BettyBundleStore,
    BettyOffer,
    BettyVariant,
    CategoryLevel,
    PromotionLabel as EvaluateArticleNewPromotionLabel,
    EvaluatePromotion as EvaluatePromotionNew,
    SellingPriceInfo
} from "../types/EvaluateArticleNew";
import {
    AvailabilityRenderData,
    Bundle,
    EvaluatePromotion as EvaluatePromotionOld,
    Location,
    Offer
} from "../types/articlesearch/Article";
import { FetchedArticle } from "../stores/ArticleStore";
import { ArticleRenderData } from "../types/RenderData";
import TrackingUtil from "./TrackingUtil";
import { ShoppingCartPosition } from "../types/ShoppingCartPosition";
import {
    getFlashSaleCounter,
    getGrossPriceWithDiscounts,
    getNetPriceWithDiscounts,
    getPieceGross,
    getPieceNet,
    isCip,
    isCipBbp,
    isDnr,
    isDnrActive,
    isManualPrice,
    isPromotion
} from "./PriceDataHelper";
import { CustomerCartItemPrices } from "../types/CartPrice";
import { SessionStore } from "../uidispatcherapi/SessionStore";

export function getBundle<
    In extends BettyVariant | FetchedArticle | ArticleRenderData,
    Out extends BettyBundle | Bundle
>(variant: In | null, selectedBundleId: string): Out | null {
    if (!variant || !variant?.bundles) {
        return null;
    }
    const bundle = variant.bundles[selectedBundleId];
    if (!bundle) {
        TrackingUtil.trackUiErrorSd(
            "articleUtil",
            "No bundle data found for id " + selectedBundleId + " and variant " + JSON.stringify(variant.variantId)
        );
        return null;
    }
    return bundle;
}

export function getBundleStoreData(
    variant: BettyVariant | FetchedArticle | ArticleRenderData,
    selectedStore: string | undefined | null,
    selectedBundleId: string
): BettyBundleStore | null {
    const bundle = getBundle<typeof variant, BettyBundle>(variant, selectedBundleId);
    if (!bundle) {
        return null;
    }
    const storeData = selectedStore && bundle.stores ? bundle.stores[selectedStore] : null;
    if (!storeData) {
        return null;
    }
    return storeData;
}

export function getPrice(
    price: PriceAndPromotionInfo,
    priceInBasket: PriceAndPromotionInfo | undefined | null,
    isSelectedOffer: boolean,
    offerPricesAndPromotions: PriceAndPromotionInfo | undefined | null
): PriceAndPromotionInfo {
    if (offerPricesAndPromotions) {
        if (isSelectedOffer) {
            // if this was the preselected offer, the price data from the bundle can be used
            return {
                ...price,
                ...offerPricesAndPromotions
            };
        } else {
            return offerPricesAndPromotions;
        }
    } else if (priceInBasket) {
        return {
            ...priceInBasket,
            // these fields can currently not always be correctly inferred from the cart data, and it doesn't depend on the cart anyway
            isDnr: price.isDnr,
            dnrInfo: price.dnrInfo,
            summaryDnrInfo: price.summaryDnrInfo,
            flashSalesCounter: price.flashSalesCounter,
            promotionLabels: price.promotionLabels
        };
    } else {
        return { ...price };
    }
}

export function getOfferPricesAndPromotions(
    offer: BettyOffer | Offer | undefined | null,
    deliveryMode: string | undefined | null
): PriceAndPromotionInfo {
    const priceInfo = offer?.deliveryModes?.[deliveryMode ?? ""]?.price ?? ({} as any);

    return {
        valid: true,
        currency: priceInfo.currency,
        strikeThroughNet: priceInfo.strikeThroughNet,
        strikeThroughGross: priceInfo.strikeThroughGross,
        unitNet: priceInfo.unitNet,
        unitGross: priceInfo.unitGross,
        priceHiddenReason: priceInfo.priceHiddenReason
    };
}

export function getAllOfferIds(bundle: Bundle | BettyBundle): Array<string> {
    return bundle.offers ? (bundle.offers as { bettyOfferId: string }[]).map((offer) => offer.bettyOfferId) : [];
}

export function getOfferId(bundle: Bundle | BettyBundle, bettyOfferId: string | undefined | null): string | undefined {
    if (!bundle?.offers) {
        return undefined;
    }
    const offers: (BettyOffer | Offer)[] = bundle.offers;
    return offers
        .filter((offer) => offer?.bettyOfferId === bettyOfferId && offer?.offerId)
        .map((offer) => offer.offerId)
        .pop();
}

export function isMarketplaceArticle(bundle: Bundle | BettyBundle): boolean {
    const storeInfo = _.values(bundle.stores); // Should only contain one store in the UI
    return storeInfo && storeInfo.length > 0 ? storeInfo[0].selectedFulfillmentType === "MARKETPLACE" : false;
}

export function getBasketIdsForBundle(bundle: Bundle | BettyBundle | undefined | null): Array<string> {
    if (!bundle) {
        return [];
    }
    if (isMarketplaceArticle(bundle)) {
        return getAllOfferIds(bundle);
    }
    return !bundle.bundleId || !bundle.bundleId.bettyBundleId ? [] : [bundle.bundleId.bettyBundleId];
}

export function getShowsSummaryDnrInfo(
    priceAndPromotionInfo: SellingPriceInfo | PriceAndPromotionInfo | null | undefined
) {
    return (
        priceAndPromotionInfo?.summaryDnrInfo !== undefined &&
        priceAndPromotionInfo?.summaryDnrInfo !== null &&
        (Object.keys(priceAndPromotionInfo?.summaryDnrInfo?.levels || {})?.length || 0) > 1
    );
}

export function addEvaluatePromotionInfo(
    priceAndPromotionInfo: PriceAndPromotionInfo,
    evaluatePromotions: EvaluatePromotionOld[] | EvaluatePromotionNew[] | undefined | null
): PriceAndPromotionInfo {
    if (evaluatePromotions) {
        const promotionLabels: { [k: string]: PromotionLabel } | Record<string, EvaluateArticleNewPromotionLabel> =
            priceAndPromotionInfo?.promotionLabels || {};
        evaluatePromotions.forEach((ep) => {
            if (ep.promoId) {
                promotionLabels[ep.promoId] = {
                    type: ep.promoId,
                    start: null,
                    end: null,
                    displayPriority: ep.displayPriority
                };
            }
        });

        priceAndPromotionInfo.promotionLabels = promotionLabels;
    }
    return priceAndPromotionInfo;
}

export function getStoreAvailability(
    bundle: Bundle,
    storeData: BettyBundleStore | null,
    isEmployee: boolean
): AvailabilityRenderData {
    const deliveryModes = storeData?.possibleDeliveryModes ?? {};
    const availability: string | undefined | null = _.get(
        deliveryModes,
        [
            storeData?.selectedDeliveryMode || "",
            "possibleFulfillmentTypes",
            storeData?.selectedFulfillmentType || "",
            "availability"
        ],
        null
    );

    const deliveryDate: string | undefined | null = _.get(
        deliveryModes,
        [
            storeData?.selectedDeliveryMode || "",
            "possibleFulfillmentTypes",
            storeData?.selectedFulfillmentType || "",
            "relativeLeadtime",
            "deliveryDate"
        ],
        null
    );

    return {
        deliveryMode: storeData?.selectedDeliveryMode,
        deliveryDate: deliveryDate ? new Date(deliveryDate) : null,
        availability: availability,
        // This is a flag that enables EV to disable availability display in case of "emergencies".
        showAvailability: isEmployee ? bundle.showEmployeeAvailability : bundle.showCustomerAvailability,
        delisted: storeData?.delisted || false
    };
}

export function replaceSpacesAndSpecialCharactersByMinuses(str: string) {
    return (
        str
            // replace (groups of) special characters by '-'
            .replace(/[\s\&\,\;\:\/\%-]+/gi, "-")
            // remove leading/trailing '-'
            .replace(/^\-+|\-+$/gi, "")
    );
}

export function getPdpLink(bundle: Bundle | BettyBundle | null): string | null {
    if (!bundle) return null;

    const articleNumber = bundle.bettyBundleId?.articleNumber;
    const variantNumber = bundle.bettyBundleId?.variantNumber;
    const bundleNumber = bundle.bettyBundleId?.bundleNumber;

    if (!articleNumber || !variantNumber || !bundleNumber) return null;

    const truncatedArticleDescription = bundle.description ? bundle.description.substring(0, 200) : "";
    const replacedArticleDescription = replaceSpacesAndSpecialCharactersByMinuses(truncatedArticleDescription);
    let url = "/pv/" + articleNumber + "/" + variantNumber + "/" + bundleNumber;
    if (replacedArticleDescription.length > 0) {
        url += "/" + replacedArticleDescription;
    }
    return url;
}

export function getDetailLink(bundle: Bundle | BettyBundle, bundleOfferId: string | undefined | null): Location {
    const url = getPdpLink(bundle);
    const offerId = getOfferId(bundle, bundleOfferId);
    return {
        pathname: url ?? undefined,
        query: offerId ? { offerId: offerId } : undefined
    };
}

export function convertEvaluatePriceData(
    price: SellingPriceInfo,
    amountInBasket: number | undefined | null
): PriceAndPromotionInfo {
    return {
        valid: price.valid || false,
        currency: price.currency,
        strikeThroughNet: price.strikeThrough,
        strikeThroughGross: price.grossStrikeThrough,
        unitNet: getNetPriceWithDiscounts(price, amountInBasket),
        unitGross: getGrossPriceWithDiscounts(price, amountInBasket),
        unitNetWithTaxes: price.finalPricesInfo?.articleWithTaxesNet,
        unitGrossWithTaxes: price.finalPricesInfo?.articleWithTaxesGross,
        pieceNet: getPieceNet(price),
        pieceGross: getPieceGross(price),
        // pieceNet / pieceGross don't seem have 'without taxes' versions in the
        // Evaluate price data, so we're keeping them as is for now
        depositNet: (price.finalPricesInfo || {}).emptiesNet,
        depositGross: (price.finalPricesInfo || {}).emptiesGross,
        kgNet: price.kgNet,
        kgGross: price.kgGross,
        // position prices left out because they're only relevant for cart articles...
        isManualPrice: isManualPrice(price),
        isCipBbp: isCipBbp(price),
        isCip: isCip(price),
        isPromotion: isPromotion(price),
        isDnr: isDnr(price),
        isDnrActive: isDnrActive(price, amountInBasket),
        dnrInfo: price.dnrInfo,
        summaryDnrInfo: price.summaryDnrInfo,
        availablePromotions: price.availablePromotions,
        promotionLabels: price.promotionLabels,
        flashSalesCounter: getFlashSaleCounter(price),
        appliedDnrIds: [], // DNRs cannot be applied if there is no basket. Also see ShoppingCartPriceDataConverter.
        priceHiddenReason: price.priceHiddenReason
    };
}

type ArticleIdInput = {
    selectedBundleId: string;
    bettyOfferId: string | undefined | null;
    originalBundleId: string | undefined | null;
};

function calculatePriceAndPromotionInfo(
    storeData: any,
    amountToUse: number | undefined | null,
    cartItem: ShoppingCartPosition | undefined | null,
    isSelectedOffer: boolean,
    offerPricesAndPromotions: any | null
): PriceAndPromotionInfo {
    const priceFromEvaluate = convertEvaluatePriceData(
        storeData.sellingPriceInfo || { valid: false, finalPricesInfo: {} },
        amountToUse
    );

    return getPrice(priceFromEvaluate, cartItem?.prices, isSelectedOffer, offerPricesAndPromotions);
}

export function createEvaluateArticlePriceAndPromotionInfo<
    TArticle extends BettyVariant | FetchedArticle | ArticleRenderData
>(
    article: TArticle,
    ids: ArticleIdInput,
    storeId: string | undefined | null,
    amountInBasket: {
        [x: string]: number | undefined | null;
    },
    cartItem: ShoppingCartPosition | undefined | null,
    currentDnrNumbers?: Array<string>,
    dnrItems?: CustomerCartItemPrices[] | null
): PriceAndPromotionInfo {
    const { selectedBundleId } = ids;
    const bundle = getBundle<typeof article, BettyBundle>(article, selectedBundleId);
    const storeData = getBundleStoreData(article, storeId, selectedBundleId);

    if (!bundle || !storeData) {
        return {
            priceHiddenReason: null,
            valid: false
        };
    }

    const isMarketplace = isMarketplaceArticle(bundle);
    const { bettyOfferId } = ids;
    const offer: BettyOffer | undefined | null = bundle.offers?.find((o) => o.bettyOfferId === bettyOfferId) || null;
    const offerPricesAndPromotions = isMarketplace
        ? getOfferPricesAndPromotions(offer, storeData.selectedDeliveryMode)
        : null;
    const isSelectedOffer = bundle.selectedOffer === bettyOfferId;

    let priceAndPromotionInfo = calculatePriceAndPromotionInfo(
        storeData,
        amountInBasket[selectedBundleId],
        cartItem,
        isSelectedOffer,
        offerPricesAndPromotions
    );

    // Handle DNR specific pricing
    if (storeId && bundle.stores?.[storeId]?.sellingPriceInfo?.dnrInfo) {
        const dnrInfo = bundle.stores?.[storeId]?.sellingPriceInfo?.dnrInfo ?? {};
        const dnrInfoKey = Object.keys(dnrInfo)[0] ?? "";
        const isRomania = SessionStore.getCountry() === "RO";

        if (isRomania && dnrInfoKey && currentDnrNumbers?.includes(dnrInfoKey) && !amountInBasket[selectedBundleId]) {
            const dnrItem = dnrItems?.find(item => item?.appliedDnrs?.find(dnr => dnr.number === dnrInfoKey));
            if (dnrItem) {
                priceAndPromotionInfo = calculatePriceAndPromotionInfo(
                    storeData,
                    Number(dnrItem.appliedDnrs?.[0]?.currentThreshold),
                    cartItem,
                    isSelectedOffer,
                    offerPricesAndPromotions
                );

                priceAndPromotionInfo.appliedDnrs = [dnrItem.appliedDnrs?.[0]];
                priceAndPromotionInfo.appliedDnrIds = [dnrInfoKey];
                priceAndPromotionInfo.strikeThroughGross = dnrItem.bundle.strikeThrough?.gross?.amount;
                priceAndPromotionInfo.strikeThroughNet = dnrItem.bundle.strikeThrough?.net?.amount;
                priceAndPromotionInfo.pieceGross = dnrItem.single.sum?.gross?.amount;
                priceAndPromotionInfo.pieceNet = dnrItem.single.sum?.net?.amount;
            }
        }
    }

    const evaluatePromotions = storeData.selectedFTEvaluatePromotions;
    return addEvaluatePromotionInfo(priceAndPromotionInfo, evaluatePromotions);
}

export function sanitize(fieldValue: string | undefined) {
    if (!fieldValue) {
        return "-";
    }
    const replacedURI = fieldValue.replace("/", "&");
    try {
        return decodeURI(replacedURI);
    } catch (e) {
        return replacedURI;
    }
}

function extractCategoryLevels(bundle: Bundle | BettyBundle | null | undefined): CategoryLevel[] {
    if (!bundle) {
        return [];
    }
    return bundle?.categories?.at(0)?.levels || [];
}

export function getCategoryPath(bundle: Bundle | BettyBundle | null | undefined): string {
    if (!bundle) {
        return "no-category";
    }
    const categories = _.map(extractCategoryLevels(bundle), (x) => (x as React.ComponentType<any>).displayName);
    const sanitizedCategoryPath =
        _.map(categories, (x) => sanitize(x))
            .concat()
            .join("/") || null;

    return sanitizedCategoryPath ? "/" + sanitizedCategoryPath + "/" : "no-category";
}
